import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { clinicService } from '../services/api';
import { agentService } from '../services/api_agent';
import { ClinicStatus, ClinicStatusColor } from '../constants/ClinicStatus';
import Module, { ModuleDescriptions } from '../constants/Modules';
import { SUPPORTED_LOCALE_OPTIONS } from '../utils/constants';
import { formatPhoneNumberForDisplay } from '../utils/phoneFormatter';
import StatusModal from '../components/ClinicDetail/StatusModal';
import IdUpdateModal from '../components/ClinicDetail/IdUpdateModal';
import PhoneNumberInput from '../components/PhoneNumberInput';
import DocumentsSection from '../components/ClinicDetail/DocumentsSection';
import ApiTestSection from '../components/ClinicDetail/ApiTestSection';
import VoiceAssetManager from '../components/VoiceAssetManager';
import BlockConfigSection from '../components/ClinicDetail/BlockConfigSection';
import SpecialNoticesSection from '../components/ClinicDetail/SpecialNoticesSection';
import DoctorsSection from '../components/ClinicDetail/DoctorsSection';
import LocationsSection from '../components/ClinicDetail/LocationsSection';
import EntitiesSection from '../components/ClinicDetail/EntitiesSection';
import DoctorSchedulesSection from '../components/ClinicDetail/DoctorSchedulesSection';
import QAKnowledgeBaseSection from '../components/ClinicDetail/QAKnowledgeBaseSection';
import VectorQueryTestSection from '../components/ClinicDetail/VectorQueryTestSection';
import ClinicQuotaSection from '../components/ClinicDetail/ClinicQuotaSection';
import ModulesSection from '../components/ClinicDetail/ModulesSection';
import OrganizationSection from '../components/ClinicDetail/OrganizationSection';
import CopilotPolishPolicySection from '../components/ClinicDetail/CopilotPolishPolicySection';
import ConfigExportSection from '../components/ClinicDetail/ConfigExportSection';
import ApiKeysSection from '../components/ClinicDetail/ApiKeysSection';
import SGClinicAssistSection from '../components/ClinicDetail/SGClinicAssistSection';
import WaflowSettingsSection from '../components/ClinicDetail/WaflowSettingsSection';
import SGiMedSettingsSection from '../components/ClinicDetail/SGiMedSettingsSection';
import AgentScheduleSection from '../components/ClinicDetail/AgentScheduleSection';
import TestModeSection from '../components/ClinicDetail/TestModeSection';
import SkipListSection from '../components/ClinicDetail/SkipListSection';
import ClinicAnalyticsSection from '../components/ClinicDetail/ClinicAnalyticsSection';
import TokenUsageSection from '../components/ClinicDetail/TokenUsageSection';
import MediaSection from '../components/ClinicDetail/MediaSection';
import TemplatesSection from '../components/ClinicDetail/TemplatesSection';
import EvaluationSection from '../components/ClinicDetail/EvaluationSection';
import AppointmentReminderSettingsSection from '../components/ClinicDetail/AppointmentReminderSettingsSection';
import LocationSelect, { getLocationDisplayName } from '../components/common/LocationSelect';
import SubscriptionKeywordsInput from '../components/ClinicDetail/SubscriptionKeywordsInput';

// Import SVG icons
import { ReactComponent as BackArrowIcon } from '../assets/back-arrow.svg';
import { ReactComponent as RefreshIcon } from '../assets/refresh.svg';
import { ReactComponent as ConversationIcon } from '../assets/conversation.svg';
import { ReactComponent as EditIcon } from '../assets/edit.svg';
import { ReactComponent as SaveIcon } from '../assets/save.svg';
import { ReactComponent as HelpCircleIcon } from '../assets/help-circle.svg';
import { ReactComponent as PlusIcon } from '../assets/plus.svg';
import { ReactComponent as CloseIcon } from '../assets/close.svg';

// Tooltip component for field descriptions
const Tooltip = ({ text }) => {
  return (
    <div className="group relative inline-block ml-2">
      <div className="cursor-help text-gray-500">
        <HelpCircleIcon className="h-4 w-4" />
      </div>
      <div className="absolute z-10 w-48 sm:w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none bottom-full left-1/2 transform -translate-x-1/2 -translate-y-1 hidden md:block">
        {text}
        <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 left-1/2 -translate-x-1/2 -bottom-1"></div>
      </div>

      {/* Mobile tooltip - displays to the right instead of above */}
      <div className="absolute z-10 w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none left-full top-1/2 transform translate-x-1 -translate-y-1/2 md:hidden">
        {text}
        <div className="absolute w-2 h-2 bg-gray-800 transform rotate-45 -left-1 top-1/2 -translate-y-1/2"></div>
      </div>
    </div>
  );
};

// Utility function to convert line breaks to <br> tags
const formatTextWithLineBreaks = (text) => {
  if (!text) return '';
  return text.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      {index < text.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));
};

const ClinicDetail = () => {
  const { clinicId } = useParams();
  const navigate = useNavigate();

  // Field descriptions for tooltips
  const fieldDescriptions = {
    id: "Unique identifier for the clinic. Used in API calls and database references.",
    name: "The name of the clinic as it appears to users and administrators.",
    abbreviation: "Optional short label stored in settings (e.g. leave-a-message prefix). Leave blank if not needed.",
    conciergeNumber: "The phone number used for the clinic's concierge service. This is the main contact point for patients.",
    clinicNumber: "The WhatsApp number associated with the clinic for direct communication.",
    description: "A detailed description of the clinic, its services, and other relevant information.",
    created: "The date and time when this clinic configuration was created in the system.",
    remark: "Additional notes or comments about the clinic that are relevant for administrators.",
    voiceGreeting: "The message that will be played or spoken when a patient calls the clinic's concierge number.",
    voiceSuccess: "The message that will be played or spoken when a patient's request is successfully processed.",
    voiceFailure: "The message that will be played or spoken when a patient's request cannot be processed.",
    voiceLanguage: "The language code used for text-to-speech conversion of voice messages.",
    voiceName: "The name of the voice used for text-to-speech conversion.",
    wabaNumber: "WhatsApp Business API number used for sending messages to patients.",
    wabaTemplate: "The ID of the WhatsApp message template used for structured messages.",
    wabaTemplateLanguage: "Override the language used when sending the inbound MCTB reply template. Leave as Auto to infer from the _en suffix on the template ID.",
    wabaTemplateVariables: "Variables to be inserted into the WhatsApp message template. Should be in JSON format with key-value pairs.",
    conciergeVersion: "The version of the concierge service being used for this clinic.",
    flowiseId: "The ID of the Flowise chatflow used for this clinic's conversational AI.",
    summarizePrompt: "Instructions used to guide the AI when summarizing patient conversations.",
    extractDocuments: "Automatically extract and process documents from the clinic's data sources. This may include patient records, medical documents, and other relevant information.",
    specialNotices: "Special notices that are displayed to patients during specific time periods. These can be used for holiday hours, service disruptions, or other important announcements."
  };

  const [clinic, setClinic] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedClinic, setEditedClinic] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [documentsTableExists, setDocumentsTableExists] = useState(true);
  const [documentsTableError, setDocumentsTableError] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [newSubscription, setNewSubscription] = useState({
    phone_number: '',
    role: 'default',
    country_code: '+852', // Default country code
    description: '',
    type: 'whatsapp', // Default type
    tags: [],
    keywords: [],
    location_id: ''
  });
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [locations, setLocations] = useState([]);
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [showAddSubscriptionForm, setShowAddSubscriptionForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [showIdUpdateModal, setShowIdUpdateModal] = useState(false);
  // Add state for newly enabled modules
  const [newlyEnabledModules, setNewlyEnabledModules] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [agentPrerequisites, setAgentPrerequisites] = useState({});
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [supportedLocaleOptions, setSupportedLocaleOptions] = useState([]);

  // Common country codes
  const countryCodes = [
    { code: '+852', name: 'Hong Kong (+852)' },
    { code: '+86', name: 'China (+86)' },
    { code: '+1', name: 'USA/Canada (+1)' },
    { code: '+44', name: 'UK (+44)' },
    { code: '+65', name: 'Singapore (+65)' },
    { code: '+81', name: 'Japan (+81)' },
    { code: '+82', name: 'South Korea (+82)' },
    { code: '+61', name: 'Australia (+61)' },
    { code: '+91', name: 'India (+91)' },
    { code: '+60', name: 'Malaysia (+60)' },
    { code: '+66', name: 'Thailand (+66)' },
    { code: '+63', name: 'Philippines (+63)' }
  ];

  // Tag options from backend
  const [tagOptions, setTagOptions] = useState([
    { value: "emergency", label: "Emergency" },
    { value: "testing", label: "Testing" }
  ]);

  // Subscription tag filter state
  const [subscriptionTagFilter, setSubscriptionTagFilter] = useState({
    includeTag: null,
    excludeTag: null
  });
  const [showInactiveSubscriptions, setShowInactiveSubscriptions] = useState(false);

  // Add state for phone number fields when editing
  const [phoneFields, setPhoneFields] = useState({
    contact_number: { countryCode: '+852', phoneNumber: '' },
    clinic_whatsapp_number: { countryCode: '+852', phoneNumber: '' },
    waba_number: { countryCode: '+852', phoneNumber: '' }
  });

  // Helper function to split phone number into country code and number
  const splitPhoneNumber = (fullNumber) => {
    if (!fullNumber) return { countryCode: '+852', phoneNumber: '' };

    for (const code of countryCodes) {
      if (fullNumber.startsWith(code.code)) {
        return {
          countryCode: code.code,
          phoneNumber: fullNumber.substring(code.code.length)
        };
      }
    }
    return { countryCode: '+852', phoneNumber: fullNumber };
  };

  // Check if the documents table exists
  useEffect(() => {
    const checkDocumentsTable = async () => {
      try {
        const result = await clinicService.checkDocumentsTable();
        setDocumentsTableExists(result.exists);
        if (!result.exists) {
          setDocumentsTableError(result.error);
          console.error('Documents table does not exist:', result.error);
        }
      } catch (err) {
        console.error('Error checking documents table:', err);
        setDocumentsTableExists(false);
      }
    };

    checkDocumentsTable();
  }, []);

  useEffect(() => {
    const loadSupportedLocaleOptions = async () => {
      try {
        const options = await clinicService.getSupportedLocaleOptions();
        setSupportedLocaleOptions(Array.isArray(options) ? options : []);
      } catch (err) {
        console.error('Error loading supported locale options:', err);
        setSupportedLocaleOptions([]);
      }
    };
    loadSupportedLocaleOptions();
  }, []);

  useEffect(() => {
    // Load clinic detail
    const loadClinicDetail = async (id) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/login");
          return;
        }

        // Fetch clinic details
        const response = await clinicService.getClinicById(id);

        // Fetch tag options first so we have them available
        const tagOptionsResponse = await clinicService.getSubscriptionTagOptions(id);

        // If we have tag options, use them
        if (tagOptionsResponse.tagOptions) {
          setTagOptions(tagOptionsResponse.tagOptions);
        }

        // Fetch subscriptions with any active filters
        const subscriptionsResponse = await clinicService.getClinicSubscriptions(
          id,
          subscriptionTagFilter.includeTag,
          subscriptionTagFilter.excludeTag
        );

        // Fetch documents
        const documentsResponse = await clinicService.getClinicDocuments(id);

        // Process phone numbers to split into country code and number parts
        const clinic = response.clinic;
        const phoneFields = {};

        const processPhoneNumber = (number) => {
          if (!number) return { countryCode: '+852', phoneNumber: '' };

          // Attempt to find a matching country code
          const foundCode = countryCodes.map(c => c.code).find(code => number.startsWith(code));

          if (foundCode) {
            return {
              countryCode: foundCode,
              phoneNumber: number.substring(foundCode.length)
            };
          }

          // If no country code match, assume number is just the phone part
          return { countryCode: '+852', phoneNumber: number };
        };

        // Process each phone field
        phoneFields.contact_number = processPhoneNumber(clinic.contact_number);
        phoneFields.clinic_whatsapp_number = processPhoneNumber(clinic.clinic_whatsapp_number);
        phoneFields.waba_number = processPhoneNumber(clinic.waba_number);

        setPhoneFields(phoneFields);
        setClinic(clinic);
        setSubscriptions(subscriptionsResponse.subscriptions || []);
        setDocuments(documentsResponse.items || []);
        setSelectedStatus(clinic.status);

        // Check for asset files - commented out until implemented
        // await checkGreetingAsset(id);
      } catch (error) {
        console.error("Error loading clinic details:", error);
        setError("Failed to load clinic details");
      } finally {
        setLoading(false);
      }
    };

    if (clinicId) {
      loadClinicDetail(clinicId);
    }
  }, [clinicId, navigate, subscriptionTagFilter]);

  // Load clinic locations for subscription branch routing (meta_data.location_id)
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response = await clinicService.getClinicLocations(clinicId);
        setLocations(response.locations || []);
      } catch (err) {
        console.error('Error loading locations:', err);
      }
    };

    if (clinicId) {
      loadLocations();
    }
  }, [clinicId]);

  // Add a comprehensive refresh function that can be called after updates
  const refreshClinicData = async (showLoadingIndicator = false) => {
    try {
      if (showLoadingIndicator) {
        setLoading(true);
      }

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      // Fetch all data in parallel for better performance
      const [
        clinicResponse,
        tagOptionsResponse,
        subscriptionsResponse,
        documentsResponse
      ] = await Promise.all([
        clinicService.getClinicById(clinicId),
        clinicService.getSubscriptionTagOptions(clinicId),
        clinicService.getClinicSubscriptions(
          clinicId,
          subscriptionTagFilter.includeTag,
          subscriptionTagFilter.excludeTag
        ),
        clinicService.getClinicDocuments(clinicId)
      ]);

      // Process phone numbers to split into country code and number parts
      const clinic = clinicResponse.clinic;
      const phoneFields = {};

      const processPhoneNumber = (number) => {
        if (!number) return { countryCode: '+852', phoneNumber: '' };

        // Attempt to find a matching country code
        const foundCode = countryCodes.map(c => c.code).find(code => number.startsWith(code));

        if (foundCode) {
          return {
            countryCode: foundCode,
            phoneNumber: number.substring(foundCode.length)
          };
        }

        // If no country code match, assume number is just the phone part
        return { countryCode: '+852', phoneNumber: number };
      };

      // Process each phone field
      phoneFields.contact_number = processPhoneNumber(clinic.contact_number);
      phoneFields.clinic_whatsapp_number = processPhoneNumber(clinic.clinic_whatsapp_number);
      phoneFields.waba_number = processPhoneNumber(clinic.waba_number);

      // Update all state with fresh data
      setPhoneFields(phoneFields);
      setClinic(clinic);
      setSubscriptions(subscriptionsResponse.subscriptions || []);
      setDocuments(documentsResponse.items || []);
      setSelectedStatus(clinic.status);

      // If we have tag options, use them
      if (tagOptionsResponse.tagOptions) {
        setTagOptions(tagOptionsResponse.tagOptions);
      }

      console.log('Clinic data refreshed successfully');
    } catch (error) {
      console.error("Error refreshing clinic data:", error);
      setError("Failed to refresh clinic data");
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  // Get URL hash for potential direct navigation to a tab
  useEffect(() => {
    // Check if the initial tab should be set based on URL hash
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['basic', 'config', 'copilot', 'blockconfig', 'specialnotices', 'doctors', 'doctor-schedules', 'locations', 'entities', 'qaknowledgebase', 'documents', 'media', 'apitest', 'analytics', 'export', 'templates', 'evaluations', 'appointment-reminders'];

    if (hash && validTabs.includes(hash)) {
      // Set as initial tab, but this will be verified when clinic data loads
      setActiveTab(hash);
    }
  }, []);

  // Verify tab access after clinic data loads
  useEffect(() => {
    if (clinic && activeTab) {
      // Check if the current tab requires a module that's not enabled
      if (activeTab === 'qaknowledgebase' && !clinic.settings?.modules?.includes(Module.QA_KNOWLEDGE_BASE) && !clinic.settings?.modules?.includes(Module.RAG_KNOWLEDGE_BASE)) {
        console.log('QA Knowledge Base module not enabled, redirecting to basic tab');
        setActiveTab('basic');
      }

      // Update URL hash to reflect current tab (for bookmarking)
      if (window.location.hash !== `#${activeTab}`) {
        window.history.replaceState(null, '', `#${activeTab}`);
      }
    }
  }, [clinic, activeTab]);

  // Load available agents from the agent API
  useEffect(() => {
    const fetchAvailableAgents = async () => {
      try {
        setIsLoadingAgents(true);
        const response = await agentService.listAgents();
        if (response.success && response.data) {
          // Store agent prerequisites
          const prerequisites = {};
          const agentNames = Object.keys(response.data).sort();

          agentNames.forEach(name => {
            // Use the enhanced prerequisites_info if available, otherwise fall back to prerequisites
            prerequisites[name] = {
              modules: response.data[name].prerequisites || [],
              info: response.data[name].prerequisites_info || []
            };
          });

          setAgentPrerequisites(prerequisites);
          setAvailableAgents(agentNames);
        } else {
          console.error('Failed to load agents:', response.error);
        }
      } catch (err) {
        console.error('Error fetching available agents:', err);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    fetchAvailableAgents();
  }, []);

  const handleUpdateStatus = () => {
    // Open the status selection modal
    setShowStatusModal(true);
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
  };

  const handleStatusConfirm = async () => {
    if (!selectedStatus) return;

    try {
      setLoading(true);
      const data = await clinicService.updateClinicStatus(clinicId, selectedStatus);
      setClinic(data.clinic);
      setSuccessMessage(`Clinic status updated to ${selectedStatus} successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Close the modal and reset selected status
      setShowStatusModal(false);
      setSelectedStatus(null);

      // Refresh all clinic data to ensure consistency
      await refreshClinicData();
    } catch (err) {
      console.error(`Error updating clinic ${clinicId} status:`, err);
      setError('Failed to update clinic status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusCancel = () => {
    setShowStatusModal(false);
    setSelectedStatus(null);
  };

  const handleUpdateId = () => {
    // Open the ID update modal
    setShowIdUpdateModal(true);
  };

  const handleIdUpdateConfirm = async (newId) => {
    try {
      setLoading(true);
      const data = await clinicService.updateClinicId(clinicId, newId);

      // Show success message
      setSuccessMessage(`Clinic ID updated successfully to ${newId}`);
      setTimeout(() => setSuccessMessage(''), 3000);

      // Close the modal
      setShowIdUpdateModal(false);

      // Redirect to the new clinic detail page
      navigate(`/clinics/${newId}`);
    } catch (err) {
      console.error(`Error updating clinic ${clinicId} ID:`, err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleIdUpdateCancel = () => {
    setShowIdUpdateModal(false);
  };

  const handleEditClick = () => {
    const contactNumberParts = splitPhoneNumber(clinic.contact_number || '');
    const clinicNumberParts = splitPhoneNumber(clinic.clinic_whatsapp_number || '');
    const wabaNumberParts = splitPhoneNumber(clinic.waba_number || '');

    setPhoneFields({
      contact_number: contactNumberParts,
      clinic_whatsapp_number: clinicNumberParts,
      waba_number: wabaNumberParts
    });

    // Ensure settings is properly initialized
    const settings = clinic.settings || {};
    const modules = settings.modules || [];

    setEditedClinic({
      ...clinic,
      remark: clinic.remark || '',
      // Ensure waba_template_variables is properly initialized
      waba_template_variables: clinic.waba_template_variables || {},
      // Ensure settings is properly initialized
      settings: {
        ...settings,
        modules: modules,
        agent_name: settings.agent_name || '',
        abbreviation: settings.abbreviation || '',
        default_language: settings.default_language || "zh_HK",
        supported_locales: settings.supported_locales ?? ["en_US", "zh_HK", "zh_CN"],
        voice_mail_enabled: settings.voice_mail_enabled || false,
        voice_mail_forced: settings.voice_mail_forced || false,
        webchat_enabled: settings.webchat_enabled || false,
        escalation_pause_enabled: settings.escalation_pause_enabled ?? false,
        escalation_contact_number_override_enabled: settings.escalation_contact_number_override_enabled ?? false,
        ai_self_identification_enabled: settings.ai_self_identification_enabled ?? false,
        smb_message_echo_pause_minutes: settings.smb_message_echo_pause_minutes ?? 1440,
        attachment_forwarding: settings.attachment_forwarding ?? true,
        attachment_ignored: settings.attachment_ignored ?? false,
        mctb_notification_enabled: settings.mctb_notification_enabled ?? false,
        appointment: {
          booking_lead_time_in_hours: settings.appointment?.booking_lead_time_in_hours ?? 2
        },
        integration: {
          ...settings.integration || {},
          sg_clinic_assist: {
            ...settings.integration?.sg_clinic_assist || {},
            enabled: settings.integration?.sg_clinic_assist?.enabled || false,
            client_id: settings.integration?.sg_clinic_assist?.client_id || '',
            organization_id: settings.integration?.sg_clinic_assist?.organization_id || '',
            api_key: settings.integration?.sg_clinic_assist?.api_key || '',
            hci_code: settings.integration?.sg_clinic_assist?.hci_code || ''
          },
          waflow: {
            ...settings.integration?.waflow || {},
            enabled: settings.integration?.waflow?.enabled || false,
            public_key: settings.integration?.waflow?.public_key || '',
            private_key: settings.integration?.waflow?.private_key || ''
          }
        },
        agent_schedule: {
          ...settings.agent_schedule,
          enabled: settings.agent_schedule?.enabled ?? false,
          should_use_schedule: settings.agent_schedule?.should_use_schedule ?? false,
          schedule: settings.agent_schedule?.schedule ?? {}
        },
        test_mode: {
          ...settings.test_mode,
          enabled: settings.test_mode?.enabled ?? false,
          authorized_numbers: settings.test_mode?.authorized_numbers ?? []
        },
        skip_list: {
          ...settings.skip_list,
          numbers: settings.skip_list?.numbers ?? []
        }
      }
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedClinic(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedClinic(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      // Validate JSON before saving
      if (editedClinic.waba_template_variables_error) {
        setError('Please fix the invalid JSON in WABA Template Variables before saving.');
        return;
      }

      // Ensure template variables is a proper object
      let templateVariables = editedClinic.waba_template_variables;
      if (typeof templateVariables === 'string') {
        try {
          templateVariables = templateVariables.trim() ? JSON.parse(templateVariables) : {};
        } catch (err) {
          setError('Invalid JSON format in WABA Template Variables.');
          return;
        }
      }

      setIsSaving(true);

      // Ensure settings are properly structured
      const settings = {
        ...(editedClinic.settings || {}),
        agent_name: editedClinic.settings?.agent_name || '',
        abbreviation: editedClinic.settings?.abbreviation || '',
        default_language: editedClinic.settings?.default_language || "zh_HK",
        supported_locales: editedClinic.settings?.supported_locales ?? [],
        voice_mail_enabled: editedClinic.settings?.voice_mail_enabled || false,
        voice_mail_forced: editedClinic.settings?.voice_mail_forced || false,
        webchat_enabled: editedClinic.settings?.webchat_enabled || false,
        escalation_pause_enabled: editedClinic.settings?.escalation_pause_enabled ?? false,
        escalation_contact_number_override_enabled: editedClinic.settings?.escalation_contact_number_override_enabled ?? false,
        ai_self_identification_enabled: editedClinic.settings?.ai_self_identification_enabled ?? false,
        smb_message_echo_pause_minutes: editedClinic.settings?.smb_message_echo_pause_minutes ?? 1440,
        attachment_forwarding: editedClinic.settings?.attachment_forwarding ?? true,
        attachment_ignored: editedClinic.settings?.attachment_ignored ?? false,
        mctb_notification_enabled: editedClinic.settings?.mctb_notification_enabled ?? false,
        modules: editedClinic.settings?.modules || [],
        appointment: {
          booking_lead_time_in_hours: editedClinic.settings?.appointment?.booking_lead_time_in_hours ?? 2
        },
        integration: {
          ...editedClinic.settings?.integration || {},
          sg_clinic_assist: {
            enabled: editedClinic.settings?.integration?.sg_clinic_assist?.enabled || false,
            client_id: editedClinic.settings?.integration?.sg_clinic_assist?.client_id || '',
            organization_id: editedClinic.settings?.integration?.sg_clinic_assist?.organization_id || '',
            api_key: editedClinic.settings?.integration?.sg_clinic_assist?.api_key || '',
            hci_code: editedClinic.settings?.integration?.sg_clinic_assist?.hci_code || ''
          },
          waflow: {
            enabled: editedClinic.settings?.integration?.waflow?.enabled || false,
            public_key: editedClinic.settings?.integration?.waflow?.public_key || '',
            private_key: editedClinic.settings?.integration?.waflow?.private_key || ''
          },
          sgi_med: {
            enabled: editedClinic.settings?.integration?.sgi_med?.enabled || false,
            refresh_token: editedClinic.settings?.integration?.sgi_med?.refresh_token || ''
          }
        },
        agent_schedule: {
          enabled: editedClinic.settings?.agent_schedule?.enabled ?? false,
          should_use_schedule: editedClinic.settings?.agent_schedule?.should_use_schedule ?? false,
          schedule: editedClinic.settings?.agent_schedule?.schedule ?? {}
        },
        test_mode: {
          enabled: editedClinic.settings?.test_mode?.enabled ?? false,
          authorized_numbers: (editedClinic.settings?.test_mode?.authorized_numbers ?? [])
            .map((number) => (number || '').trim())
            .filter(Boolean)
        },
        skip_list: {
          numbers: (editedClinic.settings?.skip_list?.numbers ?? [])
            .map((number) => (number || '').trim())
            .filter(Boolean)
        }
      };

      // Combine country codes and phone numbers
      const updatedClinic = {
        ...editedClinic,
        contact_number: phoneFields.contact_number.countryCode + phoneFields.contact_number.phoneNumber,
        clinic_whatsapp_number: phoneFields.clinic_whatsapp_number.countryCode + phoneFields.clinic_whatsapp_number.phoneNumber,
        waba_number: phoneFields.waba_number.countryCode + phoneFields.waba_number.phoneNumber,
        waba_template_variables: templateVariables,
        settings: settings
      };

      const data = await clinicService.updateClinic(clinicId, updatedClinic);

      // Check if modules changed to determine if we need to handle tab visibility
      const oldModules = clinic.settings?.modules || [];
      const newModules = settings.modules || [];

      // Find newly enabled modules
      const enabledModules = newModules.filter(m => !oldModules.includes(m));

      // Check if QA Knowledge Base module was added or removed
      const qaWasEnabled = oldModules.includes(Module.QA_KNOWLEDGE_BASE);
      const qaIsEnabled = newModules.includes(Module.QA_KNOWLEDGE_BASE);

      // Set newly enabled modules for visual indication
      if (enabledModules.length > 0) {
        setNewlyEnabledModules(enabledModules);
        // Clear the highlight after 5 seconds
        setTimeout(() => {
          setNewlyEnabledModules([]);
        }, 5000);
      }

      // If QA module was added, switch to that tab to show it to the user
      if (!qaWasEnabled && qaIsEnabled) {
        setSuccessMessage('Clinic configuration updated successfully. QA Knowledge Base module is now enabled.');
        // Set a small timeout to ensure the DOM updates first
        setTimeout(() => {
          setActiveTab('qaknowledgebase');
        }, 50);
      }
      // If QA module was removed and we're on that tab, switch to basic tab
      else if (qaWasEnabled && !qaIsEnabled && activeTab === 'qaknowledgebase') {
        setActiveTab('basic');
        setSuccessMessage('Clinic configuration updated successfully. QA Knowledge Base module is now disabled.');
      }
      else {
        setSuccessMessage('Clinic configuration updated successfully');
      }

      // Update local state with received data
      setClinic(data.clinic);
      setIsEditing(false);

      // Refresh all clinic data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error updating clinic ${clinicId}:`, err);
      setError('Failed to update clinic configuration. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const validatePhoneNumber = (phoneNumber) => {
    // Basic validation for phone number format (without country code)
    // Accepts digits only
    const phoneRegex = /^\d{5,15}$/;

    if (!phoneNumber) {
      return 'Phone number is required';
    }

    if (!phoneRegex.test(phoneNumber)) {
      return 'Invalid format. Use digits only for the phone number.';
    }

    return '';
  };

  const validateEmail = (email) => {
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      return 'Email address is required';
    }

    if (!emailRegex.test(email)) {
      return 'Invalid email format. Please enter a valid email address.';
    }

    return '';
  };

  const handleNewSubscriptionChange = (e) => {
    const { name, value } = e.target;

    if (name === 'type') {
      // When switching between email and whatsapp, reset relevant fields
      setPhoneNumberError('');
      setNewSubscription(prev => ({
        ...prev,
        phone_number: '',
        [name]: value
      }));
    } else if (name === 'phone_number') {
      // Clear previous error
      setPhoneNumberError('');

      if (newSubscription.type === 'whatsapp') {
        // Only allow digits for phone number
        const sanitizedValue = value.replace(/\D/g, '');
        setNewSubscription(prev => ({
          ...prev,
          [name]: sanitizedValue
        }));
      } else {
        // For email type, allow full email input
        setNewSubscription(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setNewSubscription(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddSubscription = async () => {
    // Validate based on subscription type
    let error = '';
    if (newSubscription.type === 'whatsapp') {
      error = validatePhoneNumber(newSubscription.phone_number);
    } else if (newSubscription.type === 'email') {
      error = validateEmail(newSubscription.phone_number);
    }

    if (error) {
      setPhoneNumberError(error);
      return;
    }

    try {
      setIsSaving(true);
      let contactValue = newSubscription.phone_number;

      // Only prepend country code for whatsapp type
      if (newSubscription.type === 'whatsapp') {
        contactValue = newSubscription.country_code + newSubscription.phone_number;
      }

      const data = await clinicService.addClinicSubscription(clinicId, {
        target: contactValue,
        role: newSubscription.role,
        description: newSubscription.description,
        type: newSubscription.type,
        tags: newSubscription.tags,
        keywords: newSubscription.keywords || [],
        meta_data: {
          location_id: newSubscription.location_id ? parseInt(newSubscription.location_id, 10) : null
        }
      });

      setSubscriptions(prev => [...prev, data.subscription]);
      setNewSubscription({
        phone_number: '',
        role: 'default',
        country_code: '+852', // Reset to default
        description: '',
        type: 'whatsapp', // Reset to default
        tags: [],
        keywords: [],
        location_id: ''
      });
      setSuccessMessage('Subscription added successfully');

      // Refresh subscription data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error adding subscription to clinic ${clinicId}:`, err);
      setError('Failed to add subscription. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditSubscription = (subscription) => {
    // For whatsapp type, extract country code and phone number
    // For email type, just use the value directly
    if (subscription.type === 'whatsapp') {
      const { countryCode, phoneNumber } = splitPhoneNumber(subscription.target);
      setEditingSubscription({
        ...subscription,
        country_code: countryCode,
        phone_number: phoneNumber,
        // Make sure type has a default value if not present
        type: subscription.type || 'whatsapp',
        // Description can be null
        description: subscription.description || '',
        // Get tags array
        tags: subscription.tags || [],
        keywords: subscription.keywords || [],
        // Branch routing location id from meta_data
        location_id: subscription.meta_data?.location_id ?? ''
      });
    } else {
      // For email type, no need to split
      setEditingSubscription({
        ...subscription,
        country_code: '+852', // Default, not used for email
        phone_number: subscription.target,
        // Make sure type has a default value if not present
        type: subscription.type || 'email',
        // Description can be null
        description: subscription.description || '',
        // Get tags array
        tags: subscription.tags || [],
        keywords: subscription.keywords || [],
        // Branch routing location id from meta_data
        location_id: subscription.meta_data?.location_id ?? ''
      });
    }
  };

  const handleEditSubscriptionChange = (e) => {
    const { name, value } = e.target;

    if (name === 'type') {
      // When switching between email and whatsapp, reset phone_number field
      setEditingSubscription(prev => ({
        ...prev,
        phone_number: '',
        [name]: value
      }));
    } else if (name === 'phone_number') {
      if (editingSubscription.type === 'whatsapp') {
        // Only allow digits for phone number
        const sanitizedValue = value.replace(/\D/g, '');
        setEditingSubscription(prev => ({
          ...prev,
          [name]: sanitizedValue
        }));
      } else {
        // For email type, allow full email input
        setEditingSubscription(prev => ({
          ...prev,
          [name]: value
        }));
      }
    } else {
      setEditingSubscription(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleUpdateSubscription = async () => {
    // Validate based on subscription type
    let error = '';
    if (editingSubscription.type === 'whatsapp') {
      error = validatePhoneNumber(editingSubscription.phone_number);
    } else if (editingSubscription.type === 'email') {
      error = validateEmail(editingSubscription.phone_number);
    }

    if (error) {
      setError(error);
      return;
    }

    try {
      setIsSaving(true);
      let contactValue = editingSubscription.phone_number;

      // Only prepend country code for whatsapp type
      if (editingSubscription.type === 'whatsapp') {
        contactValue = editingSubscription.country_code + editingSubscription.phone_number;
      }

      const data = await clinicService.updateClinicSubscription(
        clinicId,
        editingSubscription.id,
        {
          target: contactValue,
          role: editingSubscription.role,
          is_active: editingSubscription.is_active,
          description: editingSubscription.description,
          type: editingSubscription.type,
          tags: editingSubscription.tags,
          keywords: editingSubscription.keywords || [],
          meta_data: {
            ...(editingSubscription.meta_data || {}),
            location_id: editingSubscription.location_id ? parseInt(editingSubscription.location_id, 10) : null
          }
        }
      );
      setSubscriptions(prev =>
        prev.map(sub => sub.id === editingSubscription.id ? data.subscription : sub)
      );
      setEditingSubscription(null);
      setSuccessMessage('Subscription updated successfully');

      // Refresh subscription data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error updating subscription for clinic ${clinicId}:`, err);
      setError('Failed to update subscription. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleSubscriptionStatus = async (subscriptionId, currentStatus) => {
    try {
      setIsSaving(true);
      const data = await clinicService.updateClinicSubscription(
        clinicId,
        subscriptionId,
        { is_active: !currentStatus }
      );
      setSubscriptions(prev =>
        prev.map(sub => sub.id === subscriptionId ? data.subscription : sub)
      );
      setSuccessMessage('Subscription status updated successfully');

      // Refresh subscription data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error toggling subscription status for clinic ${clinicId}:`, err);
      setError('Failed to update subscription status. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to disable this subscription? This action can be undone by editing the subscription.')) {
      return;
    }

    try {
      setIsSaving(true);
      await clinicService.deleteClinicSubscription(clinicId, subscriptionId);
      // Update the subscription status in the list instead of removing it
      setSubscriptions(prev =>
        prev.map(sub =>
          sub.id === subscriptionId
            ? { ...sub, is_active: false }
            : sub
        )
      );
      setSuccessMessage('Subscription disabled successfully');

      // Refresh subscription data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error disabling subscription for clinic ${clinicId}:`, err);
      setError('Failed to disable subscription. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleExtractDocuments = async () => {
    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to extract documents for this clinic? This process may take some time.')) {
      return;
    }

    try {
      setIsExtracting(true);
      setError(null);
      const result = await clinicService.extractDocuments(clinicId);

      // Refresh documents list after extraction
      const docsData = await clinicService.getClinicDocuments(clinicId);
      setDocuments(docsData.items || []);

      setSuccessMessage('Documents extracted successfully');

      // Refresh all clinic data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error extracting documents for clinic ${clinicId}:`, err);
      setError('Failed to extract documents. Please try again later.');
    } finally {
      setIsExtracting(false);
    }
  };

  const handleAddDocument = async (newDocument) => {
    try {
      setIsSaving(true);
      const data = await clinicService.addClinicDocument(clinicId, newDocument);
      setDocuments(prev => [...prev, data.document]);
      setSuccessMessage('Document added successfully');

      // Refresh documents data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error adding document to clinic ${clinicId}:`, err);
      setError('Failed to add document. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateDocument = async (editingDocument) => {
    try {
      setIsSaving(true);
      const data = await clinicService.updateClinicDocument(
        clinicId,
        editingDocument.id,
        {
          name: editingDocument.name,
          document: editingDocument.document
        }
      );
      setDocuments(prev =>
        prev.map(doc => doc.id === editingDocument.id ? data.document : doc)
      );
      setSuccessMessage('Document updated successfully');

      // Refresh documents data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error updating document for clinic ${clinicId}:`, err);
      setError('Failed to update document. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setIsSaving(true);
      await clinicService.deleteClinicDocument(clinicId, documentId);
      setDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setSuccessMessage('Document deleted successfully');

      // Refresh documents data to ensure consistency
      await refreshClinicData();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error(`Error deleting document for clinic ${clinicId}:`, err);
      setError('Failed to delete document. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle tag filter changes
  const handleTagFilterChange = async (type, value) => {
    setSubscriptionTagFilter(prev => {
      // If clicking on already selected tag, clear it
      if (prev[type] === value) {
        return { ...prev, [type]: null };
      }
      // Otherwise set it
      return { ...prev, [type]: value };
    });
  };

  // Add a tab selection handler to verify module access
  const handleTabSelection = (tabName) => {
    // Check module requirements for this tab
    if (tabName === 'qaknowledgebase' && !clinic?.settings?.modules?.includes(Module.QA_KNOWLEDGE_BASE) && !clinic?.settings?.modules?.includes(Module.RAG_KNOWLEDGE_BASE)) {
      // Module not enabled, show message and don't switch
      setError('QA Knowledge Base module is not enabled for this clinic. Enable it in Configuration to access this tab.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Clear any error messages
    if (error) setError(null);

    // Set the active tab
    setActiveTab(tabName);

    // Save the active tab in localStorage for persistence
    localStorage.setItem('clinicDetailActiveTab', tabName);

    // If this tab requires newly enabled modules, refresh the page
    if (newlyEnabledModules.length > 0 &&
      ((tabName === 'qaknowledgebase' && newlyEnabledModules.includes(Module.QA_KNOWLEDGE_BASE)) ||
        (tabName === 'doctors' && newlyEnabledModules.includes(Module.STRUCTURED_DATA)))) {
      window.location.reload();
    }
  };

  // Check if a clinic has all prerequisites for an agent
  const hasAllPrerequisites = (agentName) => {
    if (!agentPrerequisites[agentName] || !agentPrerequisites[agentName].modules.length) {
      return true; // No prerequisites needed
    }

    const enabledModules = isEditing
      ? (editedClinic.settings?.modules || [])
      : (clinic.settings?.modules || []);

    return agentPrerequisites[agentName].modules.every(prerequisite =>
      enabledModules.includes(prerequisite)
    );
  };

  // Get missing prerequisites for an agent
  const getMissingPrerequisites = (agentName) => {
    if (!agentPrerequisites[agentName] || !agentPrerequisites[agentName].modules.length) {
      return []; // No prerequisites needed
    }

    const enabledModules = isEditing
      ? (editedClinic.settings?.modules || [])
      : (clinic.settings?.modules || []);

    return agentPrerequisites[agentName].modules.filter(prerequisite =>
      !enabledModules.includes(prerequisite)
    );
  };

  // Format missing prerequisites for display with friendly descriptions
  const formatMissingPrerequisites = (agentName) => {
    const missing = getMissingPrerequisites(agentName);
    if (missing.length === 0) return '';

    // Use the enhanced info if available
    if (agentPrerequisites[agentName].info.length > 0) {
      const missingInfo = agentPrerequisites[agentName].info.filter(
        info => missing.includes(info.module)
      );
      return `Requires ${missingInfo.map(info => info.description).join(', ')}`;
    }

    // Fallback to basic module descriptions
    return `Requires ${missing.map(m => ModuleDescriptions[m] || m).join(', ')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-500">Loading clinic details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  if (!clinic) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-gray-500">Clinic not found.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white p-3 md:p-6 rounded-lg shadow-md max-w-full overflow-hidden">
        {/* Breadcrumb navigation */}
        <div className="mb-4 flex items-center text-sm text-gray-500 overflow-x-auto">
          <Link to="/" className="hover:text-blue-500 whitespace-nowrap">Dashboard</Link>
          <span className="mx-2">/</span>
          <Link to="/clinics" className="hover:text-blue-500 whitespace-nowrap">Clinics</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-medium whitespace-nowrap">{clinic.name}</span>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start mb-8 gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/clinics"
              className="p-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center justify-center mr-2"
              title="Back to Clinics"
            >
              <BackArrowIcon className="h-5 w-5" />
            </Link>
            <h2 className="text-xl md:text-2xl font-bold">{clinic.name}</h2>
            {clinic.status && (
              <span className={`px-3 py-1 text-sm rounded-full ${ClinicStatusColor[clinic.status] || 'bg-gray-100 text-gray-800'}`}>
                {clinic.status}
              </span>
            )}
            <button
              onClick={handleUpdateStatus}
              className={`px-2 py-0.5 rounded hover:opacity-90 transition-colors duration-200 flex items-center text-xs text-white bg-blue-600`}
              title="Update Status"
            >
              <RefreshIcon className="h-3 w-3 mr-1" />
              Update Status
            </button>
            <button
              onClick={() => refreshClinicData(true)}
              className="px-2 py-0.5 rounded hover:opacity-90 transition-colors duration-200 flex items-center text-xs text-white bg-green-600"
              title="Refresh Data"
              disabled={loading}
            >
              <RefreshIcon className="h-3 w-3 mr-1" />
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          <div className="flex flex-wrap gap-3 w-full md:w-auto">
            {/* Icon buttons in the top bar */}
            <Link
              to={`/clinics/${clinic.id}/conversations`}
              className="hidden md:flex p-2 bg-blue-500 text-white rounded hover:bg-blue-600 items-center justify-center gap-2 transition-colors duration-200"
              title="View Conversations"
            >
              <ConversationIcon className="h-5 w-5" />
              <span>Conversations</span>
            </Link>
            {!isEditing ? (
              <button
                onClick={handleEditClick}
                className="p-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-2 transition-colors duration-200 flex-1 md:flex-auto"
                title="Edit Configuration"
              >
                <EditIcon className="h-5 w-5" />
                <span>Edit</span>
              </button>
            ) : (
              <button
                onClick={handleSaveChanges}
                className="p-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center justify-center gap-2 transition-colors duration-200 flex-1 md:flex-auto"
                title="Save Configuration"
              >
                <SaveIcon className="h-5 w-5" />
                <span>Save</span>
              </button>
            )}
          </div>
        </div>

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b mb-6 overflow-x-auto">
          <nav className="flex space-x-2 md:space-x-4 min-w-max pb-1">
            <button
              onClick={() => handleTabSelection('basic')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'basic'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Basic Info
            </button>
            <button
              onClick={() => handleTabSelection('config')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'config'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Configuration
            </button>
            <button
              onClick={() => handleTabSelection('copilot')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'copilot'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Copilot
            </button>
            <button
              onClick={() => handleTabSelection('blockconfig')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'blockconfig'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Block Config
            </button>
            <button
              onClick={() => handleTabSelection('specialnotices')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'specialnotices'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Notices
            </button>
            {/* Only show Doctors tab if the STRUCTURED_DATA module is enabled */}
            {clinic?.settings?.modules?.includes(Module.STRUCTURED_DATA) && (
              <button
                onClick={() => handleTabSelection('doctors')}
                className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'doctors'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Doctors
              </button>
            )}
            {/* Only show Doctor Schedules tab if the STRUCTURED_DATA module is enabled */}
            {clinic?.settings?.modules?.includes(Module.STRUCTURED_DATA) && (
              <button
                onClick={() => handleTabSelection('doctor-schedules')}
                className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'doctor-schedules'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Schedules
              </button>
            )}
            {/* Only show Locations tab if the STRUCTURED_DATA module is enabled */}
            {clinic?.settings?.modules?.includes(Module.STRUCTURED_DATA) && (
              <button
                onClick={() => handleTabSelection('locations')}
                className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'locations'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  }`}
              >
                Locations
              </button>
            )}
            <button
              onClick={() => handleTabSelection('entities')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'entities'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Entities
            </button>
            {/* Only show QA Knowledge Base tab if the module is enabled */}
            {clinic?.settings?.modules?.includes(Module.QA_KNOWLEDGE_BASE) && (
              <button
                onClick={() => handleTabSelection('qaknowledgebase')}
                className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap relative ${activeTab === 'qaknowledgebase'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
                  } ${newlyEnabledModules.includes(Module.QA_KNOWLEDGE_BASE)
                    ? 'animate-pulse bg-blue-50'
                    : ''
                  }`}
              >
                QA Knowledge Base
                {newlyEnabledModules.includes(Module.QA_KNOWLEDGE_BASE) && (
                  <span className="absolute top-0 right-0 h-2 w-2 bg-blue-500 rounded-full"></span>
                )}
              </button>
            )}

            {/* Vector Query Test Tab */}
            <button
              onClick={() => handleTabSelection('vectorquerytest')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'vectorquerytest'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Vector Test
            </button>
            <button
              onClick={() => handleTabSelection('clinicquota')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'clinicquota'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Clinic Quota
            </button>
            <button
              onClick={() => handleTabSelection('documents')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'documents'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Documents
            </button>
            <button
              onClick={() => handleTabSelection('media')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'media'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Media
            </button>
            <button
              onClick={() => handleTabSelection('apikeys')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'apikeys'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              API Keys
            </button>
            <button
              onClick={() => handleTabSelection('apitest')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'apitest'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              API Test
            </button>
            <button
              onClick={() => handleTabSelection('analytics')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'analytics'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Analytics
            </button>
            <button
              onClick={() => handleTabSelection('tokenusage')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'tokenusage'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Token Usage
            </button>
            <button
              onClick={() => handleTabSelection('export')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'export'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              導出
            </button>
            <button
              onClick={() => handleTabSelection('templates')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'templates'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Templates
            </button>
            <button
              onClick={() => handleTabSelection('evaluations')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'evaluations'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Evaluations
            </button>
            <button
              onClick={() => handleTabSelection('appointment-reminders')}
              className={`py-2 px-2 md:px-4 text-sm md:text-base whitespace-nowrap ${activeTab === 'appointment-reminders'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Reminders
            </button>
          </nav>
        </div>

        <div className="space-y-8">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <div className="bg-gray-50 p-3 md:p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Basic Information</h3>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={editedClinic.name}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Abbreviation <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={editedClinic.settings?.abbreviation || ''}
                      onChange={(e) => setEditedClinic({
                        ...editedClinic,
                        settings: {
                          ...editedClinic.settings,
                          abbreviation: e.target.value,
                        },
                      })}
                      placeholder="e.g. Diestel, OPH_PP"
                      className="w-full p-2 border rounded"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Saved to settings. Used as a short label in notifications.
                    </p>
                  </div>

                  <PhoneNumberInput
                    label="Concierge Number"
                    countryCode={phoneFields.contact_number.countryCode}
                    phoneNumber={phoneFields.contact_number.phoneNumber}
                    onCountryCodeChange={(code) => setPhoneFields(prev => ({
                      ...prev,
                      contact_number: { ...prev.contact_number, countryCode: code }
                    }))}
                    onPhoneNumberChange={(number) => setPhoneFields(prev => ({
                      ...prev,
                      contact_number: { ...prev.contact_number, phoneNumber: number }
                    }))}
                    showAvailableNumbers={true}
                  />

                  <PhoneNumberInput
                    label="WABA Number"
                    countryCode={phoneFields.waba_number.countryCode}
                    phoneNumber={phoneFields.waba_number.phoneNumber}
                    onCountryCodeChange={(code) => setPhoneFields(prev => ({
                      ...prev,
                      waba_number: { ...prev.waba_number, countryCode: code }
                    }))}
                    onPhoneNumberChange={(number) => setPhoneFields(prev => ({
                      ...prev,
                      waba_number: { ...prev.waba_number, phoneNumber: number }
                    }))}
                  />

                  <PhoneNumberInput
                    label="Clinic Number"
                    countryCode={phoneFields.clinic_whatsapp_number.countryCode}
                    phoneNumber={phoneFields.clinic_whatsapp_number.phoneNumber}
                    onCountryCodeChange={(code) => setPhoneFields(prev => ({
                      ...prev,
                      clinic_whatsapp_number: { ...prev.clinic_whatsapp_number, countryCode: code }
                    }))}
                    onPhoneNumberChange={(number) => setPhoneFields(prev => ({
                      ...prev,
                      clinic_whatsapp_number: { ...prev.clinic_whatsapp_number, phoneNumber: number }
                    }))}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={editedClinic.description || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      rows="3"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region
                    </label>
                    <select
                      name="region"
                      value={editedClinic.region || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Select Region</option>
                      <option value="HK">Hong Kong (HK)</option>
                      <option value="SG">Singapore (SG)</option>
                      <option value="JP">Japan (JP)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remark
                    </label>
                    <textarea
                      name="remark"
                      value={editedClinic.remark || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      rows="3"
                    ></textarea>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-4">
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">ID:</div>
                    <div className="flex items-center">
                      <span className="mr-2">{clinic.id}</span>
                      <button
                        onClick={handleUpdateId}
                        className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        title="Update Clinic ID"
                      >
                        Edit ID
                      </button>
                      <Tooltip text={fieldDescriptions.id} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Name:</div>
                    <div className="flex items-center">
                      <span>{clinic.name}</span>
                      <Tooltip text={fieldDescriptions.name} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Abbreviation:</div>
                    <div className="flex items-center">
                      <span>{clinic.settings?.abbreviation || "—"}</span>
                      <Tooltip text={fieldDescriptions.abbreviation} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Concierge Number:</div>
                    <div className="flex items-center">
                      <span>
                        {formatPhoneNumberForDisplay(clinic.contact_number)}
                      </span>
                      <Tooltip text={fieldDescriptions.conciergeNumber} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">WABA Number:</div>
                    <div className="flex items-center">
                      <span>
                        {formatPhoneNumberForDisplay(clinic.waba_number)}
                      </span>
                      <Tooltip text={fieldDescriptions.wabaNumber} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Clinic Number:</div>
                    <div className="flex items-center">
                      <span>
                        {formatPhoneNumberForDisplay(clinic.clinic_whatsapp_number)}
                      </span>
                      <Tooltip text={fieldDescriptions.clinicNumber} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4 md:pt-1">Description:</div>
                    <div className="flex items-start">
                      <span className="mr-2">{clinic.description || 'No description'}</span>
                      <Tooltip text={fieldDescriptions.description} />
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Region:</div>
                    <div className="flex items-center">
                      <span>
                        {                         clinic.region === 'HK' ? 'Hong Kong (HK)' :
                         clinic.region === 'SG' ? 'Singapore (SG)' :
                         clinic.region === 'JP' ? 'Japan (JP)' :
                         'No region specified'}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col md:flex-row md:items-center">
                    <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4">Created:</div>
                    <div className="flex items-center">
                      <span>{new Date(clinic.created_at).toLocaleString()}</span>
                      <Tooltip text={fieldDescriptions.created} />
                    </div>
                  </div>

                  {clinic.remark && (
                    <div className="flex flex-col md:flex-row">
                      <div className="font-medium w-full md:w-32 mb-1 md:mb-0 md:mr-4 md:pt-1">Remark:</div>
                      <div className="flex items-start">
                        <span className="mr-2 whitespace-pre-line">{clinic.remark}</span>
                        <Tooltip text={fieldDescriptions.remark} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Configuration Tab */}
          {activeTab === 'config' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Configuration</h3>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice Greeting Message
                      <Tooltip text={fieldDescriptions.voiceGreeting} />
                    </label>
                    <textarea
                      name="voice_greeting_message"
                      value={editedClinic.voice_greeting_message || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                      rows="3"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice Language
                      <Tooltip text={fieldDescriptions.voiceLanguage} />
                    </label>
                    <input
                      type="text"
                      name="voice_language"
                      value={editedClinic.voice_language || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Voice Name
                      <Tooltip text={fieldDescriptions.voiceName} />
                    </label>
                    <input
                      type="text"
                      name="voice_name"
                      value={editedClinic.voice_name || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div className="mt-4">
                    <div className="flex items-start space-x-4">
                      <div className="flex items-center h-5">
                        <input
                          id="should_greet_with_recording"
                          name="should_greet_with_recording"
                          type="checkbox"
                          checked={editedClinic.should_greet_with_recording || false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              should_greet_with_recording: e.target.checked
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="should_greet_with_recording" className="font-medium text-gray-700">
                          Use voice recording instead of text-to-speech
                        </label>
                        <p className="text-gray-500">
                          When enabled, the system will use a pre-recorded greeting instead of generating one with text-to-speech.
                        </p>
                      </div>
                    </div>

                    <div className="flex-shrink-0 space-x-2 mt-2">
                      <VoiceAssetManager
                        assetType="greeting_voice"
                        clinicId={clinicId}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WABA Template ID
                      <Tooltip text={fieldDescriptions.wabaTemplate} />
                    </label>
                    <input
                      type="text"
                      name="waba_template_id"
                      value={editedClinic.waba_template_id || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WABA Template Language (override)
                      <Tooltip text={fieldDescriptions.wabaTemplateLanguage} />
                    </label>
                    <select
                      name="waba_template_language"
                      value={editedClinic.waba_template_language || ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded"
                    >
                      <option value="">Auto (from _en suffix)</option>
                      {SUPPORTED_LOCALE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      WABA Template Variables
                      <Tooltip text={fieldDescriptions.wabaTemplateVariables} />
                    </label>
                    <textarea
                      name="waba_template_variables"
                      value={typeof editedClinic.waba_template_variables === 'object'
                        ? JSON.stringify(editedClinic.waba_template_variables, null, 2)
                        : editedClinic.waba_template_variables || '{}'}
                      onChange={(e) => {
                        let isValidJson = true;
                        try {
                          // Try to parse as JSON
                          const jsonValue = e.target.value.trim() ? JSON.parse(e.target.value) : {};
                          setEditedClinic(prev => ({
                            ...prev,
                            waba_template_variables: jsonValue,
                            waba_template_variables_error: null
                          }));
                        } catch (err) {
                          isValidJson = false;
                          // If not valid JSON, store as string
                          setEditedClinic(prev => ({
                            ...prev,
                            waba_template_variables: e.target.value,
                            waba_template_variables_error: 'Invalid JSON format'
                          }));
                        }
                      }}
                      className={`w-full p-2 border rounded font-mono text-sm ${editedClinic.waba_template_variables_error ? 'border-red-500' : 'border-gray-300'
                        }`}
                      rows="5"
                      placeholder="{}"
                    />
                    {editedClinic.waba_template_variables_error && (
                      <p className="mt-1 text-xs text-red-500">
                        {editedClinic.waba_template_variables_error}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Enter template variables in JSON format, e.g. {"\"name\": \"value\""} or {"\"var1\": \"value1\", \"var2\": \"value2\""}.
                      Make sure to use double quotes for keys and values.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Concierge Version
                      <Tooltip text={fieldDescriptions.conciergeVersion} />
                    </label>
                    {isLoadingAgents ? (
                      <div className="p-2 border rounded bg-gray-50 text-gray-500">Loading available agents...</div>
                    ) : (
                      <>
                        <select
                          name="concierge_version"
                          value={editedClinic.concierge_version || ''}
                          onChange={handleInputChange}
                          className="w-full p-2 border rounded"
                        >
                          <option value="">Select an agent</option>
                          {/* Add the current concierge_version as an option if it's not in availableAgents */}
                          {editedClinic.concierge_version &&
                            !availableAgents.includes(editedClinic.concierge_version) && (
                              <option value={editedClinic.concierge_version}>
                                {editedClinic.concierge_version} (Current)
                              </option>
                            )}
                          {availableAgents.map((agent) => {
                            const isEnabled = hasAllPrerequisites(agent);
                            return (
                              <option
                                key={agent}
                                value={agent}
                                disabled={!isEnabled}
                                className={!isEnabled ? "text-gray-400" : ""}
                              >
                                {agent} {!isEnabled ? `(Required Modules: ${agentPrerequisites[agent].modules.join(', ')})` : ""}
                              </option>
                            );
                          })}
                        </select>

                        {/* Show prerequisite information for the selected agent */}
                        {editedClinic.concierge_version && agentPrerequisites[editedClinic.concierge_version] &&
                          agentPrerequisites[editedClinic.concierge_version].modules.length > 0 && (
                            <div className="mt-2 p-2 bg-blue-50 text-sm rounded">
                              <p className="font-medium">Required modules for {editedClinic.concierge_version}:</p>
                              <ul className="list-disc ml-5 mt-1">
                                {agentPrerequisites[editedClinic.concierge_version].info.length > 0 ?
                                  agentPrerequisites[editedClinic.concierge_version].info.map(info => (
                                    <li key={info.module} className={
                                      !editedClinic.settings?.modules?.includes(info.module)
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }>
                                      {info.description}
                                      {!editedClinic.settings?.modules?.includes(info.module) &&
                                        " (not enabled)"}
                                    </li>
                                  )) :
                                  agentPrerequisites[editedClinic.concierge_version].modules.map(module => (
                                    <li key={module} className={
                                      !editedClinic.settings?.modules?.includes(module)
                                        ? "text-red-600"
                                        : "text-green-600"
                                    }>
                                      {ModuleDescriptions[module] || module}
                                      {!editedClinic.settings?.modules?.includes(module) &&
                                        " (not enabled)"}
                                    </li>
                                  ))
                                }
                              </ul>
                              {!hasAllPrerequisites(editedClinic.concierge_version) && (
                                <p className="mt-2 text-red-600 font-medium">
                                  ⚠️ This agent is unavailable because some required modules are not enabled.
                                  Please enable them in the Modules section below.
                                </p>
                              )}
                            </div>
                          )}
                      </>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Select the agent version to use for this clinic's concierge service.
                      Some agents require specific modules to be enabled.
                    </p>
                  </div>
                  {/* Advanced Settings Section */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-md font-semibold mb-4">Advanced Settings</h4>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="voice_mail_enabled"
                          name="voice_mail_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.voice_mail_enabled || false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                voice_mail_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="voice_mail_enabled" className="font-medium text-gray-700">
                          Enable Voice Mail
                        </label>
                        <Tooltip text="When enabled, the system will allow patients to leave voice messages if they cannot be assisted immediately." />
                        <p className="text-gray-500">
                          When enabled, the system will allow patients to leave voice messages if they cannot be assisted immediately.
                        </p>
                      </div>
                    </div>

                    {editedClinic.settings?.voice_mail_enabled && (
                      <div className="flex space-x-4 mb-4">
                        <VoiceAssetManager
                          assetType="voice_mail_voice"
                          clinicId={clinicId}
                          checkButtonText="Check Voice Mail Asset"
                          uploadButtonText="Upload Asset"
                          assetFoundText="Voice mail recording found"
                          assetNotFoundText="Voice mail recording not found"
                        />
                        <VoiceAssetManager
                          assetType="voice_mail_anonymous_voice"
                          clinicId={clinicId}
                          checkButtonText="Check Anonymous Voice Mail Asset"
                          uploadButtonText="Upload Asset"
                          assetFoundText="Voice mail recording found"
                          assetNotFoundText="Voice mail recording not found"
                        />
                      </div>
                    )}

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="webchat_enabled"
                          name="webchat_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.webchat_enabled || false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                webchat_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="webchat_enabled" className="font-medium text-gray-700">
                          Enable Webchat
                        </label>
                        <Tooltip text="When enabled, matching inbound callers are routed to the webchat MCTB branch: voice greeting, then an SMS with a link to the hub passcode chat." />
                        <p className="text-gray-500">
                          When enabled, eligible callers receive an SMS linking to https://hub.nexicare.ai/chat?p={'{passcode}'} instead of the default MCTB flow.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="escalation_pause_enabled"
                          name="escalation_pause_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.escalation_pause_enabled ?? false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                escalation_pause_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="escalation_pause_enabled" className="font-medium text-gray-700">
                          Pause AI on Escalation
                        </label>
                        <Tooltip text="When enabled, escalating to human support pauses the AI for 15 minutes so the clinic can take over. When disabled, the branch is still notified but the AI keeps responding." />
                        <p className="text-gray-500">
                          When enabled, escalations pause the AI for 15 minutes so staff can take over. When disabled, the branch is still notified but the AI keeps answering the customer.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="escalation_contact_number_override_enabled"
                          name="escalation_contact_number_override_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.escalation_contact_number_override_enabled ?? false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                escalation_contact_number_override_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="escalation_contact_number_override_enabled" className="font-medium text-gray-700">
                          Use Collected Contact Number for Escalation Link
                        </label>
                        <Tooltip text="When enabled, if the AI collected a callback contact number (e.g. the case is for a family member), the escalation WhatsApp link points to that number instead of the messaging customer. The dialing code is sanitized to the clinic's region." />
                        <p className="text-gray-500">
                          When enabled, the escalation WhatsApp link uses the contact number the AI collected (sanitized to the clinic's region) instead of the messaging customer's number. When disabled, the link always points to the customer messaging.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="ai_self_identification_enabled"
                          name="ai_self_identification_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.ai_self_identification_enabled ?? false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                ai_self_identification_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="ai_self_identification_enabled" className="font-medium text-gray-700">
                          Allow AI to Identify as AI Assistant
                        </label>
                        <Tooltip text="When enabled, the AI always introduces itself as the clinic's AI assistant on its first reply, overriding personas (e.g. the Japanese reception-desk persona) that otherwise present as the clinic's reception desk. When disabled, the AI follows the clinic's persona." />
                        <p className="text-gray-500">
                          When enabled, the AI openly introduces itself as the clinic's AI assistant, overriding personas that present as a human reception desk. When disabled, the AI follows the clinic's persona.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="voice_mail_forced"
                          name="voice_mail_forced"
                          type="checkbox"
                          checked={editedClinic.settings?.voice_mail_forced || false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                voice_mail_forced: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="voice_mail_forced" className="font-medium text-gray-700">
                          Force Voice Mail
                        </label>
                        <Tooltip text="When enabled, the system will force all calls to go to voice mail, bypassing the normal call flow." />
                        <p className="text-gray-500">
                          When enabled, the system will force all calls to go to voice mail, bypassing the normal call flow.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="attachment_forwarding"
                          name="attachment_forwarding"
                          type="checkbox"
                          checked={editedClinic.settings?.attachment_forwarding ?? true}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                attachment_forwarding: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="attachment_forwarding" className="font-medium text-gray-700">
                          Enable Forward Attachment
                        </label>
                        <Tooltip text="When enabled, attachments will be saved and forwarded to the clinic as public media links instead of replying I don't understand." />
                        <p className="text-gray-500">
                          When enabled, attachments will be saved and forwarded to the clinic as public media links instead of replying I don't understand.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="attachment_ignored"
                          name="attachment_ignored"
                          type="checkbox"
                          checked={editedClinic.settings?.attachment_ignored ?? false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                attachment_ignored: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="attachment_ignored" className="font-medium text-gray-700">
                          Ignore Attachment
                        </label>
                        <Tooltip text="When enabled, attachment messages are ignored by the AI: no reply is sent and nothing is forwarded to the clinic. This overrides Forward Attachment." />
                        <p className="text-gray-500">
                          When enabled, attachment messages are ignored by the AI: no reply is sent and nothing is forwarded to the clinic. Overrides Forward Attachment.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4 mb-4">
                      <div className="flex items-center h-5">
                        <input
                          id="mctb_notification_enabled"
                          name="mctb_notification_enabled"
                          type="checkbox"
                          checked={editedClinic.settings?.mctb_notification_enabled ?? false}
                          onChange={(e) => {
                            setEditedClinic(prev => ({
                              ...prev,
                              settings: {
                                ...prev.settings || {},
                                mctb_notification_enabled: e.target.checked
                              }
                            }));
                          }}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                      </div>
                      <div className="ml-3 text-sm">
                        <label htmlFor="mctb_notification_enabled" className="font-medium text-gray-700">
                          Enable MCTB Notification
                        </label>
                        <Tooltip text="When enabled: only after a missed call takes the WhatsApp MCTB path and the customer welcome template has been sent, clinic staff are notified. Does not apply to voice mail or anonymous voice mail." />
                        <p className="text-gray-500">
                          When enabled, clinic staff are notified only after the WhatsApp MCTB welcome template has been sent to the customer. Voice mail and anonymous voice mail are not affected.
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent Name (optional)
                        <Tooltip text="Optional name the AI uses to refer to itself (e.g. Nancy). Leave blank for a generic assistant." />
                      </label>
                      <input
                        type="text"
                        name="agent_name"
                        value={editedClinic.settings?.agent_name || ''}
                        onChange={(e) => {
                          setEditedClinic(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings || {},
                              agent_name: e.target.value
                            }
                          }));
                        }}
                        placeholder="e.g. Nancy"
                        className="w-full p-2 border rounded"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Optional name the AI uses to refer to itself (e.g. Nancy). Leave blank for a generic assistant.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Default Language
                        <Tooltip text="The default language used for communication with patients." />
                      </label>
                      <select
                        name="default_language"
                        value={editedClinic.settings?.default_language || "zh_HK"}
                        onChange={(e) => {
                          setEditedClinic(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings || {},
                              default_language: e.target.value
                            }
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      >
                        {supportedLocaleOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        The default language for this clinic's communication.
                      </p>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Supported Locales
                          <Tooltip text="Locales this clinic supports for patient communication." />
                        </label>
                        <div className="flex gap-2 text-xs">
                          <button
                            type="button"
                            onClick={() => {
                              const allValues = supportedLocaleOptions.map((o) => o.value);
                              setEditedClinic(prev => ({
                                ...prev,
                                settings: {
                                  ...prev.settings || {},
                                  supported_locales: allValues
                                }
                              }));
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Select all
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => {
                              setEditedClinic(prev => ({
                                ...prev,
                                settings: {
                                  ...prev.settings || {},
                                  supported_locales: []
                                }
                              }));
                            }}
                            className="text-gray-500 hover:text-gray-700 hover:underline"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                      <div className="border border-gray-200 rounded-md p-3 bg-gray-50/50 space-y-2 max-h-48 overflow-y-auto">
                        {supportedLocaleOptions.map((opt) => {
                          const selected = (editedClinic.settings?.supported_locales ?? []).includes(opt.value);
                          return (
                            <div key={opt.value} className="flex items-center">
                              <input
                                id={`supported_locale_${opt.value}`}
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => {
                                  const current = editedClinic.settings?.supported_locales ?? [];
                                  const next = e.target.checked
                                    ? [...current, opt.value]
                                    : current.filter((v) => v !== opt.value);
                                  setEditedClinic(prev => ({
                                    ...prev,
                                    settings: {
                                      ...prev.settings || {},
                                      supported_locales: next
                                    }
                                  }));
                                }}
                                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                              />
                              <label
                                htmlFor={`supported_locale_${opt.value}`}
                                className="ml-3 text-sm text-gray-700 cursor-pointer select-none"
                              >
                                {opt.label}
                              </label>
                            </div>
                          );
                        })}
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Choose which locales this clinic supports for patient communication.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Appointment Booking Lead Time (hours)
                        <Tooltip text="The minimum number of hours in advance that a patient must book an appointment." />
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="168"
                        name="booking_lead_time"
                        value={editedClinic.settings?.appointment?.booking_lead_time_in_hours ?? 2}
                        onChange={(e) => {
                          const parsedValue = parseInt(e.target.value, 10);
                          const value = isNaN(parsedValue) ? 2 : parsedValue;
                          setEditedClinic(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings || {},
                              appointment: {
                                ...(prev.settings?.appointment || {}),
                                booking_lead_time_in_hours: value
                              }
                            }
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        The minimum number of hours in advance that a patient must book an appointment.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        SMB Message Echo Pause (minutes)
                        <Tooltip text="How long the conversation is paused when a business account message echo (smb_message_echo) is received. Default 1440 (24 hours)." />
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="smb_message_echo_pause_minutes"
                        value={editedClinic.settings?.smb_message_echo_pause_minutes ?? 1440}
                        onChange={(e) => {
                          const parsedValue = parseInt(e.target.value, 10);
                          const value = isNaN(parsedValue) ? 1440 : parsedValue;
                          setEditedClinic(prev => ({
                            ...prev,
                            settings: {
                              ...prev.settings || {},
                              smb_message_echo_pause_minutes: value
                            }
                          }));
                        }}
                        className="w-full p-2 border rounded"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Minutes to pause a conversation when a business account replies directly (smb_message_echo).
                      </p>
                    </div>
                  </div>

                  {/* SG Clinic Assist Integration Settings */}
                  <SGClinicAssistSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* Waflow Integration Settings */}
                  <WaflowSettingsSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* SG iMed Integration Settings */}
                  <SGiMedSettingsSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* Agent Schedule Settings */}
                  <AgentScheduleSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* Test Mode Settings */}
                  <TestModeSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* Skip List Settings */}
                  <SkipListSection
                    clinic={clinic}
                    editedClinic={editedClinic}
                    setEditedClinic={setEditedClinic}
                    isEditing={isEditing}
                  />

                  {/* Organization settings (team collaboration mode) */}
                  <OrganizationSection organizationId={clinic?.organization_id} />

                  {/* Module Settings */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <ModulesSection
                      modules={editedClinic.settings?.modules || []}
                      onChange={(updatedModules) => {
                        setEditedClinic(prev => ({
                          ...prev,
                          settings: {
                            ...prev.settings,
                            modules: updatedModules
                          }
                        }));
                      }}
                    />
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <button
                      onClick={handleSaveChanges}
                      disabled={isSaving}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-4">
                  <p className="flex items-start">
                    <span className="font-medium w-32 mr-4">Voice Greeting:</span>
                    <div className="flex-grow">
                      <div className="flex items-start justify-between">
                        <div className="flex-grow">
                          <span
                            className={`px-2 py-1 text-xs rounded-full ${clinic.should_greet_with_recording
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                              }`}
                          >
                            {clinic.should_greet_with_recording ? 'Recording' : 'Text-to-Speech'}
                          </span>
                          <span>{clinic.voice_greeting_message || 'Not set'}</span>
                        </div>
                      </div>
                    </div>
                    <Tooltip text={fieldDescriptions.voiceGreeting} />
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium w-32">Voice Language:</span>
                    <span>{clinic.voice_language || 'Not set'}</span>
                    <Tooltip text={fieldDescriptions.voiceLanguage} />
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium w-32">Voice Name:</span>
                    <span>{clinic.voice_name || 'Not set'}</span>
                    <Tooltip text={fieldDescriptions.voiceName} />
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium w-32">WABA Template:</span>
                    <span>{clinic.waba_template_id || 'Not set'}</span>
                    <Tooltip text={fieldDescriptions.wabaTemplate} />
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium w-32">WABA Language:</span>
                    <span>
                      {clinic.waba_template_language
                        ? (SUPPORTED_LOCALE_OPTIONS.find((opt) => opt.value === clinic.waba_template_language)?.label || clinic.waba_template_language)
                        : 'Auto (from _en suffix)'}
                    </span>
                    <Tooltip text={fieldDescriptions.wabaTemplateLanguage} />
                  </p>
                  <p className="flex items-start">
                    <span className="font-medium w-32">WABA Variables:</span>
                    <span className="font-mono text-sm bg-gray-50 p-2 rounded max-w-xl overflow-auto whitespace-pre">
                      {clinic.waba_template_variables ?
                        typeof clinic.waba_template_variables === 'object'
                          ? JSON.stringify(clinic.waba_template_variables, null, 2)
                          : clinic.waba_template_variables
                        : 'Not set'}
                    </span>
                    <Tooltip text={fieldDescriptions.wabaTemplateVariables} />
                  </p>
                  <p className="flex items-center">
                    <span className="font-medium w-32">Concierge Ver:</span>
                    <span>{clinic.concierge_version || 'Not set'}</span>
                    <Tooltip text={fieldDescriptions.conciergeVersion} />
                  </p>
                  {/* Advanced Settings in View Mode */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="text-md font-semibold mb-4">Advanced Settings</h4>

                    <p className="flex items-center">
                      <span className="font-medium w-32">Voice Mail:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${clinic.settings?.voice_mail_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {clinic.settings?.voice_mail_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, the system will allow patients to leave voice messages if they cannot be assisted immediately." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Webchat:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${clinic.settings?.webchat_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {clinic.settings?.webchat_enabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, matching inbound callers are routed to the webchat MCTB branch (SMS link to hub passcode chat)." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Pause on Escalation:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.escalation_pause_enabled ?? false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {(clinic.settings?.escalation_pause_enabled ?? false) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, escalating to human support pauses the AI for 15 minutes so the clinic can take over. When disabled, the branch is still notified but the AI keeps responding." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Escalation Contact Link:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.escalation_contact_number_override_enabled ?? false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {(clinic.settings?.escalation_contact_number_override_enabled ?? false) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, the escalation WhatsApp link uses the contact number the AI collected (sanitized to the clinic's region) instead of the messaging customer's number." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">AI Self-Identification:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.ai_self_identification_enabled ?? false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {(clinic.settings?.ai_self_identification_enabled ?? false) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, the AI always introduces itself as the clinic's AI assistant, overriding personas that present as a human reception desk. When disabled, the AI follows the clinic's persona." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Forward Attachment:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.attachment_forwarding ?? true) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        {(clinic.settings?.attachment_forwarding ?? true) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, attachments will be saved and forwarded to the clinic as public media links instead of replying I don't understand." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Ignore Attachment:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.attachment_ignored ?? false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        {(clinic.settings?.attachment_ignored ?? false) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled, attachment messages are ignored by the AI: no reply is sent and nothing is forwarded to the clinic. Overrides Forward Attachment." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">MCTB Notification:</span>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${(clinic.settings?.mctb_notification_enabled ?? false) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        {(clinic.settings?.mctb_notification_enabled ?? false) ? 'Enabled' : 'Disabled'}
                      </span>
                      <Tooltip text="When enabled: only after a missed call takes the WhatsApp MCTB path and the customer welcome template has been sent, clinic staff are notified. Does not apply to voice mail or anonymous voice mail." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Agent Name:</span>
                      <span>{clinic.settings?.agent_name || "—"}</span>
                      <Tooltip text="Optional name the AI uses to refer to itself (e.g. Nancy). Leave blank for a generic assistant." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Default Language:</span>
                      <span>
                        {supportedLocaleOptions.find((o) => o.value === (clinic.settings?.default_language || "zh_HK"))?.label ?? clinic.settings?.default_language ?? "zh_HK"}
                      </span>
                      <Tooltip text="The default language used for communication with patients." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Supported Locales:</span>
                      <span>
                        {(clinic.settings?.supported_locales?.length)
                          ? clinic.settings.supported_locales
                              .map((code) => supportedLocaleOptions.find((o) => o.value === code)?.label ?? code)
                              .join(", ")
                          : "—"}
                      </span>
                      <Tooltip text="Locales this clinic supports for patient communication." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">Booking Lead Time:</span>
                      <span>{clinic.settings?.appointment?.booking_lead_time_in_hours ?? 2} hours</span>
                      <Tooltip text="The minimum number of hours in advance that a patient must book an appointment." />
                    </p>

                    <p className="flex items-center mt-2">
                      <span className="font-medium w-32">SMB Echo Pause:</span>
                      <span>{clinic.settings?.smb_message_echo_pause_minutes ?? 1440} minutes</span>
                      <Tooltip text="How long the conversation is paused when a business account replies directly (smb_message_echo)." />
                    </p>

                    {/* SG Clinic Assist Integration in View Mode */}
                    <SGClinicAssistSection
                      clinic={clinic}
                      editedClinic={clinic}
                      setEditedClinic={() => { }}
                      isEditing={false}
                    />

                    {/* Agent Schedule in View Mode */}
                    <AgentScheduleSection
                      clinic={clinic}
                      editedClinic={clinic}
                      setEditedClinic={() => {}}
                      isEditing={false}
                    />

                    {/* Test Mode in View Mode */}
                    <TestModeSection
                      clinic={clinic}
                      editedClinic={clinic}
                      setEditedClinic={() => {}}
                      isEditing={false}
                    />

                    {/* Skip List in View Mode */}
                    <SkipListSection
                      clinic={clinic}
                      editedClinic={clinic}
                      setEditedClinic={() => {}}
                      isEditing={false}
                    />

                    <OrganizationSection organizationId={clinic?.organization_id} />

                    {/* Modules Section in View Mode */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <ModulesSection
                        modules={clinic.settings?.modules || []}
                        readOnly={true}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Subscriptions Section */}
              <div className="mt-8 border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Assistant Subscriptions</h3>

                <div className="mb-6 p-3 md:p-4 border rounded bg-white">
                  {/* Header with Add Button */}
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                    <h3 className="text-lg font-medium">Manage Subscriptions</h3>
                    <button
                      onClick={() => setShowAddSubscriptionForm(!showAddSubscriptionForm)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
                    >
                      {showAddSubscriptionForm ? (
                        <>
                          <CloseIcon className="h-4 w-4 mr-1" />
                          Hide Form
                        </>
                      ) : (
                        <>
                          <PlusIcon className="h-4 w-4 mr-1" />
                          Add New Subscription
                        </>
                      )}
                    </button>
                  </div>

                  {/* Add New Subscription Form - Only shown when showAddSubscriptionForm is true */}
                  {showAddSubscriptionForm && (
                    <div className="mb-6 p-3 md:p-4 border rounded bg-white">
                      <h4 className="text-md font-medium mb-3">Add New Subscription</h4>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            name="type"
                            value={newSubscription.type}
                            onChange={handleNewSubscriptionChange}
                            className="w-full p-2 border rounded"
                          >
                            <option value="whatsapp">WhatsApp</option>
                            <option value="email">Email</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {newSubscription.type === 'whatsapp' ? 'Phone Number' : 'Email Address'}
                          </label>
                          {newSubscription.type === 'whatsapp' ? (
                            <div className="flex flex-col sm:flex-row sm:space-x-2 space-y-2 sm:space-y-0">
                              <div className="w-full sm:w-1/3">
                                <select
                                  name="country_code"
                                  value={newSubscription.country_code}
                                  onChange={handleNewSubscriptionChange}
                                  className="w-full p-2 border rounded"
                                >
                                  {countryCodes.map((code) => (
                                    <option key={code.code} value={code.code}>
                                      {code.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="w-full sm:w-2/3">
                                <input
                                  type="text"
                                  name="phone_number"
                                  value={newSubscription.phone_number}
                                  onChange={handleNewSubscriptionChange}
                                  className={`w-full p-2 border rounded ${phoneNumberError ? 'border-red-500' : ''}`}
                                  placeholder="Enter phone number (digits only)"
                                />
                              </div>
                            </div>
                          ) : (
                            <input
                              type="email"
                              name="phone_number"
                              value={newSubscription.phone_number}
                              onChange={handleNewSubscriptionChange}
                              className={`w-full p-2 border rounded ${phoneNumberError ? 'border-red-500' : ''}`}
                              placeholder="Enter email address"
                            />
                          )}
                          {phoneNumberError && (
                            <p className="mt-1 text-sm text-red-600">{phoneNumberError}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-500">
                            {newSubscription.type === 'whatsapp'
                              ? 'Enter the phone number without spaces or special characters'
                              : 'Enter a valid email address'}
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Role
                          </label>
                          <select
                            name="role"
                            value={newSubscription.role}
                            onChange={handleNewSubscriptionChange}
                            className="w-full p-2 border rounded"
                          >
                            <option value="default">Default</option>
                            <option value="admin">Admin</option>
                            <option value="staff">Staff</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description (Optional)
                          </label>
                          <input
                            type="text"
                            name="description"
                            value={newSubscription.description}
                            onChange={handleNewSubscriptionChange}
                            className="w-full p-2 border rounded"
                            placeholder="Enter a description for this subscription"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Visit Location (Optional)
                          </label>
                          <LocationSelect
                            name="location_id"
                            locations={locations}
                            value={newSubscription.location_id}
                            onChange={handleNewSubscriptionChange}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Route branch escalations and appointment requests to this subscription. Leave as None for clinic-wide.
                          </p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tags
                          </label>
                          <div className="flex flex-wrap gap-2">
                            {tagOptions.map((tagOption) => (
                              <label key={tagOption.value} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  name="tags"
                                  value={tagOption.value}
                                  checked={newSubscription.tags.includes(tagOption.value)}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setNewSubscription(prev => ({
                                      ...prev,
                                      tags: e.target.checked
                                        ? [...prev.tags, value]
                                        : prev.tags.filter(tag => tag !== value)
                                    }));
                                  }}
                                  className="mr-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                                <span className="text-gray-700">{tagOption.label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Voicemail Keywords (Optional)
                          </label>
                          <SubscriptionKeywordsInput
                            value={newSubscription.keywords}
                            onChange={(keywords) => setNewSubscription(prev => ({ ...prev, keywords }))}
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            If set, this WhatsApp number only receives voicemail alerts when the transcript contains any of these keywords. Leave empty to receive all voicemails.
                          </p>
                        </div>
                        <button
                          onClick={handleAddSubscription}
                          disabled={isSaving || !newSubscription.phone_number}
                          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                        >
                          {isSaving ? 'Adding...' : 'Add Subscription'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Subscription List */}
                  <div className="space-y-4">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
                      <h4 className="text-md font-medium">Existing Subscriptions</h4>

                      {/* Tag filter UI */}
                      <div className="flex flex-col sm:flex-row gap-4 overflow-x-auto pb-2">
                        <div>
                          <span className="text-sm text-gray-700 mr-2">Include tag:</span>
                          <div className="inline-flex flex-wrap gap-1 mt-1">
                            {tagOptions.map(tag => {
                              const getTagClasses = (tagValue, isActive) => {
                                if (!isActive) return 'bg-gray-100 text-gray-700 hover:bg-gray-200';

                                if (tagValue === 'emergency' || tagValue === 'emergency only') {
                                  return 'bg-red-100 text-red-800 border border-red-300';
                                }
                                if (tagValue === 'portal_only') {
                                  return 'bg-purple-100 text-purple-800 border border-purple-300';
                                }
                                return 'bg-blue-100 text-blue-800 border border-blue-300';
                              };
                              return (
                                <button
                                  key={`include-${tag.value}`}
                                  onClick={() => handleTagFilterChange('includeTag', tag.value)}
                                  className={`px-2 py-1 text-xs rounded-full transition-colors ${getTagClasses(tag.value, subscriptionTagFilter.includeTag === tag.value)}`}
                                >
                                  {tag.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <span className="text-sm text-gray-700 mr-2">Exclude tag:</span>
                          <div className="inline-flex flex-wrap gap-1 mt-1">
                            {tagOptions.map(tag => {
                              const getTagClasses = (tagValue, isActive) => {
                                if (!isActive) return 'bg-gray-100 text-gray-700 hover:bg-gray-200';

                                if (tagValue === 'emergency' || tagValue === 'emergency only') {
                                  return 'bg-red-100 text-red-800 border border-red-300';
                                }
                                if (tagValue === 'portal_only') {
                                  return 'bg-purple-100 text-purple-800 border border-purple-300';
                                }
                                return 'bg-blue-100 text-blue-800 border border-blue-300';
                              };
                              return (
                                <button
                                  key={`exclude-${tag.value}`}
                                  onClick={() => handleTagFilterChange('excludeTag', tag.value)}
                                  className={`px-2 py-1 text-xs rounded-full transition-colors ${getTagClasses(tag.value, subscriptionTagFilter.excludeTag === tag.value)}`}
                                >
                                  {tag.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Show Inactive Subscriptions Toggle */}
                        <div className="flex items-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={showInactiveSubscriptions}
                              onChange={(e) => setShowInactiveSubscriptions(e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              showInactiveSubscriptions ? 'bg-blue-600' : 'bg-gray-300'
                            }`}>
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                showInactiveSubscriptions ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            </div>
                            <span className="ml-2 text-sm text-gray-700">Show Inactive</span>
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Filter subscriptions based on showInactiveSubscriptions toggle */}
                    {(() => {
                      const filteredSubscriptions = showInactiveSubscriptions
                        ? subscriptions
                        : subscriptions.filter(sub => sub.is_active);

                      if (filteredSubscriptions.length === 0) {
                        return (
                          <p className="text-gray-500">
                            {showInactiveSubscriptions
                              ? 'No subscriptions found for this clinic.'
                              : 'No active subscriptions found. Toggle "Show Inactive" to see disabled subscriptions.'}
                          </p>
                        );
                      }

                      return (
                        <>
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Note:</strong> Disabled subscriptions are shown with a gray background. You can re-enable them using the "Enable" button or edit their status.
                              {!showInactiveSubscriptions && (
                                <span className="block mt-1">
                                  <strong>Tip:</strong> Toggle "Show Inactive" to see and manage disabled subscriptions.
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap">Contact</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden sm:table-cell">Type</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden md:table-cell">Role</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden lg:table-cell">Description</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden md:table-cell">Tags</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden lg:table-cell">Keywords</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden md:table-cell">Branch</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap">Status</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap hidden lg:table-cell">Created</th>
                                  <th className="py-2 px-4 border text-left whitespace-nowrap">Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredSubscriptions.map((subscription) => (
                              <tr key={subscription.id} className={`border-t ${!subscription.is_active ? 'bg-gray-50' : ''}`}>
                                {editingSubscription && editingSubscription.id === subscription.id ? (
                                  <>
                                    <td className="py-2 px-4 border">
                                      {editingSubscription.type === 'whatsapp' ? (
                                        <div className="flex flex-col sm:flex-row gap-1">
                                          <div className="w-full sm:w-2/5">
                                            <select
                                              name="country_code"
                                              value={editingSubscription.country_code}
                                              onChange={handleEditSubscriptionChange}
                                              className="w-full p-1 border rounded text-sm"
                                            >
                                              {countryCodes.map((code) => (
                                                <option key={code.code} value={code.code}>
                                                  {code.name}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <div className="w-full sm:w-3/5">
                                            <input
                                              type="text"
                                              name="phone_number"
                                              value={editingSubscription.phone_number}
                                              onChange={handleEditSubscriptionChange}
                                              className="w-full p-1 border rounded"
                                              placeholder="Enter phone number (digits only)"
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <input
                                          type="email"
                                          name="phone_number"
                                          value={editingSubscription.phone_number}
                                          onChange={handleEditSubscriptionChange}
                                          className="w-full p-1 border rounded"
                                          placeholder="Enter email address"
                                        />
                                      )}
                                    </td>
                                    <td className="py-2 px-4 border hidden sm:table-cell">
                                      <select
                                        name="type"
                                        value={editingSubscription.type}
                                        onChange={handleEditSubscriptionChange}
                                        className="w-full p-1 border rounded"
                                      >
                                        <option value="whatsapp">WhatsApp</option>
                                        <option value="email">Email</option>
                                      </select>
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      <select
                                        name="role"
                                        value={editingSubscription.role}
                                        onChange={handleEditSubscriptionChange}
                                        className="w-full p-1 border rounded"
                                      >
                                        <option value="default">Default</option>
                                        <option value="admin">Admin</option>
                                        <option value="staff">Staff</option>
                                      </select>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      <input
                                        type="text"
                                        name="description"
                                        value={editingSubscription.description || ''}
                                        onChange={handleEditSubscriptionChange}
                                        className="w-full p-1 border rounded"
                                        placeholder="Enter description"
                                      />
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      <div className="flex flex-wrap gap-1">
                                        {tagOptions.map((tagOption) => (
                                          <label key={tagOption.value} className="inline-flex items-center">
                                            <input
                                              type="checkbox"
                                              value={tagOption.value}
                                              checked={editingSubscription.tags.includes(tagOption.value)}
                                              onChange={(e) => {
                                                const value = e.target.value;
                                                setEditingSubscription(prev => ({
                                                  ...prev,
                                                  tags: e.target.checked
                                                    ? [...prev.tags, value]
                                                    : prev.tags.filter(tag => tag !== value)
                                                }));
                                              }}
                                              className="mr-1 h-3 w-3 text-blue-600 border-gray-300 rounded"
                                            />
                                            <span className="text-xs">{tagOption.label}</span>
                                          </label>
                                        ))}
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      <SubscriptionKeywordsInput
                                        value={editingSubscription.keywords || []}
                                        onChange={(keywords) => setEditingSubscription(prev => ({ ...prev, keywords }))}
                                        inputClassName="w-full p-1 border rounded text-sm"
                                        placeholder="Add keyword"
                                      />
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      <LocationSelect
                                        name="location_id"
                                        locations={locations}
                                        value={editingSubscription.location_id}
                                        onChange={handleEditSubscriptionChange}
                                        className="w-full p-1 border rounded"
                                      />
                                    </td>
                                    <td className="py-2 px-4 border">
                                      <select
                                        name="is_active"
                                        value={editingSubscription.is_active}
                                        onChange={handleEditSubscriptionChange}
                                        className="w-full p-1 border rounded"
                                      >
                                        <option value={true}>Active</option>
                                        <option value={false}>Inactive</option>
                                      </select>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      {new Date(subscription.created_at).toLocaleString()}
                                    </td>
                                    <td className="py-2 px-4 border">
                                      <div className="flex flex-wrap gap-1">
                                        <button
                                          onClick={handleUpdateSubscription}
                                          disabled={isSaving}
                                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                                        >
                                          {isSaving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                          onClick={() => setEditingSubscription(null)}
                                          className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="py-2 px-4 border">
                                      <div className="flex items-center">
                                        {subscription.type === 'whatsapp' ? (
                                          (() => {
                                            const { countryCode, phoneNumber } = splitPhoneNumber(subscription.target);
                                            return (
                                              <div className="flex flex-col sm:flex-row sm:items-center">
                                                <span className="font-medium sm:mr-1">{countryCode}</span>
                                                <span>{phoneNumber}</span>
                                              </div>
                                            );
                                          })()
                                        ) : (
                                          <span className="text-blue-600">{subscription.target}</span>
                                        )}
                                      </div>

                                      <div className="sm:hidden mt-1 text-xs">
                                        <div><span className="font-medium">Type:</span> {subscription.type || (subscription.target && subscription.target.includes('@') ? 'email' : 'whatsapp')}</div>
                                        <div><span className="font-medium">Role:</span> {subscription.role}</div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {subscription.tags && subscription.tags.length > 0 ?
                                            subscription.tags.map(tag => (
                                              <span key={tag} className={`px-2 py-0.5 text-xs rounded-full ${tag === 'emergency only' || tag === 'emergency' ? 'bg-red-100 text-red-800' :
                                                tag === 'testing' ? 'bg-blue-100 text-blue-800' :
                                                tag === 'portal_only' ? 'bg-purple-100 text-purple-800' :
                                                  'bg-gray-100 text-gray-800'
                                                }`}>
                                                {tag}
                                              </span>
                                            ))
                                            : ''}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 border hidden sm:table-cell">
                                      <span className="capitalize">
                                        {subscription.type || (subscription.target && subscription.target.includes('@') ? 'email' : 'whatsapp')}
                                      </span>
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      <span className="capitalize">{subscription.role}</span>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      <span>{subscription.description || '-'}</span>
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      <div className="flex flex-wrap gap-1">
                                        {subscription.tags && subscription.tags.length > 0 ?
                                          subscription.tags.map(tag => (
                                            <span key={tag} className={`px-2 py-0.5 text-xs rounded-full ${tag === 'emergency only' || tag === 'emergency' ? 'bg-red-100 text-red-800' :
                                              tag === 'testing' ? 'bg-blue-100 text-blue-800' :
                                              tag === 'portal_only' ? 'bg-purple-100 text-purple-800' :
                                                'bg-gray-100 text-gray-800'
                                              }`}>
                                              {tag}
                                            </span>
                                          ))
                                          : '-'}
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      <div className="flex flex-wrap gap-1">
                                        {subscription.keywords && subscription.keywords.length > 0 ?
                                          subscription.keywords.map(keyword => (
                                            <span key={keyword} className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-900">
                                              {keyword}
                                            </span>
                                          ))
                                          : <span className="text-gray-400 text-xs">All voicemails</span>}
                                      </div>
                                    </td>
                                    <td className="py-2 px-4 border hidden md:table-cell">
                                      {(() => {
                                        const locId = subscription.meta_data?.location_id;
                                        if (locId === null || locId === undefined || locId === '') {
                                          return <span className="text-gray-400 text-xs">Clinic-wide</span>;
                                        }
                                        const matched = locations.find(loc => String(loc.id) === String(locId));
                                        return <span className="text-sm">{matched ? getLocationDisplayName(matched) : `#${locId}`}</span>;
                                      })()}
                                    </td>
                                    <td className="py-2 px-4 border">
                                      <span
                                        className={`px-2 py-1 text-xs rounded-full ${subscription.is_active
                                          ? 'bg-green-100 text-green-800'
                                          : 'bg-red-100 text-red-800'
                                          }`}
                                      >
                                        {subscription.is_active ? 'Active' : 'Inactive'}
                                      </span>
                                    </td>
                                    <td className="py-2 px-4 border hidden lg:table-cell">
                                      {new Date(subscription.created_at).toLocaleString()}
                                    </td>
                                    <td className="py-2 px-4 border">
                                      <div className="flex flex-wrap gap-1">
                                        <button
                                          onClick={() => handleEditSubscription(subscription)}
                                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                        >
                                          Edit
                                        </button>
                                        {subscription.is_active ? (
                                          <button
                                            onClick={() => handleDeleteSubscription(subscription.id)}
                                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                          >
                                            Disable
                                          </button>
                                        ) : (
                                          <button
                                            onClick={() => handleToggleSubscriptionStatus(subscription.id, subscription.is_active)}
                                            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                                          >
                                            Enable
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'copilot' && (
            <CopilotPolishPolicySection clinicId={clinicId} />
          )}

          {/* Block Configuration Tab */}
          {activeTab === 'blockconfig' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <BlockConfigSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Special Notices Tab */}
          {activeTab === 'specialnotices' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <SpecialNoticesSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Doctors Tab */}
          {activeTab === 'doctors' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              {console.log('Rendering DoctorsSection for clinic:', clinicId)}
              <DoctorsSection clinicId={clinicId} clinic={clinic} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Locations Tab */}
          {activeTab === 'locations' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <LocationsSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Entities Tab */}
          {activeTab === 'entities' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <EntitiesSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Doctor Schedules Tab */}
          {activeTab === 'doctor-schedules' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <DoctorSchedulesSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* {JSON.stringify(clinic.settings.modules)} */}

          {/* QA Knowledge Base Tab - only rendered if module is enabled */}
          {activeTab === 'qaknowledgebase' && (clinic?.settings?.modules?.includes(Module.QA_KNOWLEDGE_BASE) || clinic?.settings?.modules?.includes(Module.RAG_KNOWLEDGE_BASE)) && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <QAKnowledgeBaseSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Vector Query Test Tab */}
          {activeTab === 'vectorquerytest' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <VectorQueryTestSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {activeTab === 'clinicquota' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <ClinicQuotaSection clinicId={clinicId} />
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <DocumentsSection
              clinic={clinic}
              documents={documents}
              documentsTableExists={documentsTableExists}
              documentsTableError={documentsTableError}
              onAddDocument={handleAddDocument}
              onUpdateDocument={handleUpdateDocument}
              onDeleteDocument={handleDeleteDocument}
              onExtractDocuments={handleExtractDocuments}
              isSaving={isSaving}
              isExtracting={isExtracting}
              fieldDescriptions={fieldDescriptions}
              onDataChange={refreshClinicData}
            />
          )}

          {/* Media Tab */}
          {activeTab === 'media' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <MediaSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* API Keys Tab */}
          {activeTab === 'apikeys' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <ApiKeysSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* API Test Tab */}
          {activeTab === 'apitest' && (
            <ApiTestSection clinic={clinic} onDataChange={refreshClinicData} />
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <ClinicAnalyticsSection clinicId={clinicId} clinicName={clinic.name} />
            </div>
          )}

          {/* Token Usage Tab */}
          {activeTab === 'tokenusage' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <TokenUsageSection clinicId={clinicId} clinicName={clinic.name} />
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <ConfigExportSection clinicId={clinicId} onDataChange={refreshClinicData} />
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <TemplatesSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {/* Evaluations Tab */}
          {activeTab === 'evaluations' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <EvaluationSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}

          {activeTab === 'appointment-reminders' && (
            <div className="bg-gray-50 p-6 rounded-lg">
              <AppointmentReminderSettingsSection clinicId={clinicId} onDataChange={refreshClinicData} />
            </div>
          )}
        </div>
      </div>

      {/* Status Update Modal */}
      {clinic && (
        <StatusModal
          show={showStatusModal}
          clinic={clinic}
          selectedStatus={selectedStatus}
          onStatusSelect={handleStatusSelect}
          onConfirm={handleStatusConfirm}
          onCancel={handleStatusCancel}
        />
      )}

      {/* ID Update Modal */}
      {clinic && (
        <IdUpdateModal
          show={showIdUpdateModal}
          clinic={clinic}
          onConfirm={handleIdUpdateConfirm}
          onCancel={handleIdUpdateCancel}
        />
      )}
    </>
  );
};

export default ClinicDetail;
